<script setup lang="ts">
import { reactive, onBeforeMount } from 'vue';
import apiClient from '@src/api/api-client.js';
import clientIdStore from '@src/application/store/client-identifier-store.js';
import PrinterSettingsForm from '@src/client-settings/components/printer-settings-form.vue';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import TabWrapper from '@components/tab-wrapper.vue';
import Tab from '@components/tab.vue';
import Button from '@components/buttons/button.vue';
import { Printer } from '@src/printer/interfaces/printer.js';
import { cloneDeep } from 'lodash';
import { closeLoader, openLoader } from '@utils/loader-util.js';
import { addToast } from '@utils/toast-util.js';
import { PrinterSettingsResponse } from '@src/printer/interfaces/printer-settings-response.js';
import Card from '@components/card.vue';
import { useCurrentUserComposable } from '@src/user/composable/current-user-composable.ts';

const emit = defineEmits<{
    save: [],
    abort: [],
}>();

const { currentUser } = useCurrentUserComposable();

const devicePrinters = reactive<{
    zplPrinter: Printer|null,
    eposPrinter: Printer|null,
    pdfPrinter: Printer|null,
    shippinglabelPrinter: Printer|null,
}>({
    zplPrinter: null,
    eposPrinter: null,
    pdfPrinter: null,
    shippinglabelPrinter: null,
});

const userPrinters = reactive(cloneDeep(devicePrinters));

onBeforeMount(async () => {
    openLoader();
    const data: PrinterSettingsResponse = (await apiClient.get('/be/user-printer-settings')).data;
    userPrinters.zplPrinter = data.userZplPrinter;
    userPrinters.eposPrinter = data.userEposPrinter;
    userPrinters.pdfPrinter = data.userPdfPrinter;
    userPrinters.shippinglabelPrinter = data.userShippinglabelPrinter;
    devicePrinters.zplPrinter = data.deviceZplPrinter;
    devicePrinters.eposPrinter = data.deviceEposPrinter;
    devicePrinters.pdfPrinter = data.devicePdfPrinter;
    devicePrinters.shippinglabelPrinter = data.deviceShippinglabelPrinter;
    closeLoader();
});

async function save() {
    openLoader();
    const data: PrinterSettingsResponse = {
        userZplPrinter: userPrinters.zplPrinter,
        userEposPrinter: userPrinters.eposPrinter,
        userPdfPrinter: userPrinters.pdfPrinter,
        userShippinglabelPrinter: userPrinters.shippinglabelPrinter,
        deviceZplPrinter: devicePrinters.zplPrinter,
        deviceEposPrinter: devicePrinters.eposPrinter,
        devicePdfPrinter: devicePrinters.pdfPrinter,
        deviceShippinglabelPrinter: devicePrinters.shippinglabelPrinter,
    };
    await apiClient.patch('/be/user-printer-settings', data, [], true);
    closeLoader();
    addToast('Einstellungen wurden gespeichert.');
    emit('save');
}
</script>

<template>
    <div>
        <TabWrapper default-tab="userprinter">
            <Tab :label="'Für ' + currentUser!.firstName + ' ' + currentUser!.lastName" tab-name="userprinter">
                <PrinterSettingsForm :model-value="userPrinters"/>
            </Tab>
            <Tab label="Für dieses Gerät" tab-name="deviceprinter">
                <PrinterSettingsForm v-if="clientIdStore.clientId.data" :model-value="devicePrinters"/>
                <Card v-else card-icon="info-circle">
                    Einstellungen können nicht angepasst werden, da für dieses Gerät keine Client ID hinterlegt ist.
                    Bitte wende Dich an einen Administrator, wenn spezielle Einstellungen für dieses Gerät notwendig sind.
                </Card>
            </Tab>
        </TabWrapper>

        <VerticalAlignChildrenWrapper gap-top="3">
            <CenterChildrenWrapper>
                <Button context="primary" @click="save">Speichern</Button>
                <Button @click="emit('abort')">Abbrechen</Button>
            </CenterChildrenWrapper>
        </VerticalAlignChildrenWrapper>
    </div>
</template>
